import React, { Fragment, useEffect, useState } from 'react'

import { StandardPage } from '../../components/_EOS/StandardPage'
import {
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { PreSaleSku } from './entities/PreSaleSku'
import { generateTableHeadersPreSaleSku } from '.'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { Status } from '../../hooks/useFetchState'

import { getPreSaleSku } from './api'
import { usePagination } from '../../hooks/usePagination'
import { PreSaleSkuSearch } from './PreSaleSkuSearch'
import { defaultFilters } from './config'
import { Filter } from '../../components/_WMS/FilterComponent'
import { useModal } from '../../hooks/useModal'
import { EditPreSaleSkuModal } from './modals/EditPreSaleSkuModal'
import { Modal } from '../../components/_EOS/Modal'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { PreSaleSkuButtonBar } from './PreSaleSkuButtonBar'
import { EditPreSaleSkuMasiveModal } from './modals/EditPreSaleSkuMasiveModal'
import { useProductOrigins } from '../../hooks'
import { IconButton, Tooltip } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'

interface PreSaleSkuPageProps {}

export const PreSaleSkuPage: React.FC<PreSaleSkuPageProps> = () => {
  const { state, callApi } = useApi<ApiResponse<PreSaleSku>>()
  const [selectedElement, setSelectedElement] = React.useState<number[]>([])
  const [orderBy, setOrderBy] = useState<GenericOrderBy<PreSaleSku>>({
    field: '',
    direction: 'desc'
  })
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)

  const pagination = usePagination(50)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const tableHeadersPreSaleSku = generateTableHeadersPreSaleSku()

  const requestPreSaleSku = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getPreSaleSku(filters, page, pageSize, orderBy)).then(() =>
      setSelectedElement([])
    )
  }

  const handleSorting = ({ field, direction }: GenericOrderBy<PreSaleSku>) => {
    const auxField = field === 'skuId' ? 'skuId' : field
    if (auxField === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field: auxField, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedElement((prev) => [...prev, id])
    } else {
      setSelectedElement((prev) => [...prev.filter((el) => el !== id)])
    }
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    setSelectedElement([])

    requestPreSaleSku(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestPreSaleSku(selectedFilters, 0, pagination.pageSize)
  }, [orderBy, selectedFilters])

  const editModalState = useModal<{
    entity: PreSaleSku
  }>('')
  const createModalState = useModal('Edición Masiva Stock Venta en Verde')

  const { getProductOrigins } = useProductOrigins()

  useEffect(() => {
    getProductOrigins()
  }, [])

  type CustomActionsProps = {
    id: number
    row: PreSaleSku
  }

  const MyCustomActions: React.FC<CustomActionsProps> = ({ row }) => {
    return (
      <Fragment>
        <Tooltip title='Editar' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              editModalState.openModal(
                'xs',
                { entity: row },
                'Editar Stock Venta en Verde'
              )
            }
            style={{ verticalAlign: 'bottom' }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <StandardPage pageTitle='Gestionar Stock Venta en Verde'>
        <PreSaleSkuSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestPreSaleSku}
        />
        {/* BUTTON BAR */}
        <PreSaleSkuButtonBar
          onClickCreatePreSaleSku={createModalState.openModal}
          handleCloseModal={createModalState.closeModal}
          handleSnackbar={openSnackBar}
          selectedFilters={selectedFilters}
        />
        {/* TABLE */}
        <GenericTable<PreSaleSku>
          headersConfig={tableHeadersPreSaleSku}
          IDKey={'skuId'}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
          onCheckChange={handleCheckChange}
          checkedElements={selectedElement}
          onSortChange={handleSorting}
          orderField={orderBy}
          customActionComponent={MyCustomActions}
        />
      </StandardPage>

      <Modal
        open={editModalState.isOpen}
        title={editModalState.title}
        maxWidth={editModalState.size}
        onClose={editModalState.closeModal}
      >
        <EditPreSaleSkuModal
          preSaleSku={editModalState.extendedProps?.entity as PreSaleSku}
          handleSnackbar={openSnackBar}
          handleCloseModal={editModalState.closeModal}
          requestPreSaleSku={() => {
            requestPreSaleSku(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>
      <Modal
        open={createModalState.isOpen}
        title={createModalState.title}
        maxWidth={createModalState.size}
        onClose={createModalState.closeModal}
        cancelButton={{
          action: createModalState.closeModal,
          title: 'Salir'
        }}
      >
        <EditPreSaleSkuMasiveModal
          handleSnackbar={openSnackBar}
          handleCloseModal={createModalState.closeModal}
          requestWorkOrderRecall={() => {
            requestPreSaleSku(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
